import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FormControlLabel, Checkbox, Radio, FormGroup, Box, IconButton, Collapse, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material/styles';
import { Cell, LabelList } from "recharts";
import WindDirectionArrow from './WindDirectionArrow';
import DownloadGraph from './DownloadGraph';
import { format } from 'date-fns';

// Helper to find highlights
const getHighlights = (data, dataKey) => {
  if (!data || data.length === 0) return {};
  let min = Infinity;
  let max = -Infinity;
  let minIdx = -1;
  let maxIdx = -1;
  let firstIdx = -1;
  let lastIdx = -1;

  data.forEach((d, idx) => {
    const val = typeof d[dataKey] === 'number' ? d[dataKey] : parseFloat(d[dataKey]);
    if (!isNaN(val)) {
      if (firstIdx === -1) firstIdx = idx;
      lastIdx = idx;
      if (val < min) {
        min = val;
        minIdx = idx;
      }
      if (val > max) {
        max = val;
        maxIdx = idx;
      }
    }
  });

  return { start: firstIdx, end: lastIdx, min: minIdx, max: maxIdx };
};
const getWindInfo = (speed) => {
  if (speed >= 0.00 && speed <= 0.46) return { indicator: '(C)', description: 'Calm' };
  if (speed >= 0.51 && speed <= 2.01) return { indicator: '(LA)', description: 'Light Air' };
  if (speed >= 2.06 && speed <= 3.55) return { indicator: '(LB)', description: 'Light Breeze' };
  if (speed >= 3.60 && speed <= 5.61) return { indicator: '(GB)', description: 'Gentle Breeze' };
  if (speed >= 5.66 && speed <= 8.69) return { indicator: '(MB)', description: 'Moderate Breeze' };
  if (speed >= 8.75 && speed <= 11.27) return { indicator: '(FB)', description: 'Fresh Breeze' };
  if (speed >= 11.32 && speed <= 14.35) return { indicator: '(SB)', description: 'Strong Breeze' };
  if (speed >= 14.40 && speed <= 17.44) return { indicator: '(MG)', description: 'Moderate Gale' };
  if (speed >= 17.49 && speed <= 21.04) return { indicator: '(FG)', description: 'Fresh Gale' };
  if (speed >= 21.09 && speed <= 24.64) return { indicator: '(SG)', description: 'Strong Gale' };
  if (speed >= 24.69 && speed <= 28.76) return { indicator: '(WG)', description: 'Whole Gale' };
  if (speed >= 28.81 && speed <= 32.87) return { indicator: '(S)', description: 'Storm' };
  if (speed >= 32.92 && speed <= 36.53) return { indicator: '(H)', description: 'Hurricane' };

  return { indicator: '', description: '' };
};
const CustomizedDot = (props) => {
  const { cx, cy, stroke, payload, value, dataKey, index, isActive } = props;

  // Safety check: if value is null, undefined, or NaN, do not render a dot
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  if (value === null || value === undefined || isNaN(numericValue)) {
    return null;
  }

  const highlights = props.highlights || getHighlights(props.seriesData, dataKey);
  const isStart = index === highlights.start;
  const isEnd = index === highlights.end;
  const isMin = index === highlights.min;
  const isMax = index === highlights.max;

  if (isStart || isEnd || isMin || isMax) {
    let label = "";
    let color = stroke;
    let r = 6;
    let fontSize = "9px";

    if (isMax) {
      label = "MAX";
      color = "#d32f2f";
      r = 8;
    } else if (isMin) {
      label = "MIN";
      color = "#1976d2";
      r = 8;
    } else if (isStart) {
      label = "START";
      color = "#455a64";
    } else if (isEnd) {
      label = "END";
      color = "#455a64";
    }

    // If it's a wind direction parameter, we show the arrow instead of a plain circle
    const isWd = dataKey && (dataKey.toLowerCase().startsWith('wd') || dataKey.toLowerCase().includes('_wd'));

    if (isWd) {
      return (
        <g>
          <WindDirectionArrow cx={cx} cy={cy} value={numericValue} stroke={color} size={isActive ? 24 : 20} />
          <text
            x={cx}
            y={cy - (isActive ? 14 : 12)}
            textAnchor="middle"
            fill={color}
            style={{ fontSize: fontSize, fontWeight: 'bold', pointerEvents: 'none' }}
          >
            {label}
          </text>
        </g>
      );
    }

    return (
      <g>
        <circle cx={cx} cy={cy} r={isActive ? r + 2 : r} fill={color} stroke="#fff" strokeWidth={1} />
        <text
          x={cx}
          y={cy - (isActive ? 10 : 8)}
          textAnchor="middle"
          fill={color}
          style={{ fontSize: fontSize, fontWeight: 'bold', pointerEvents: 'none' }}
        >
          {label}
        </text>
      </g>
    );
  }

  // Default rendering (no highlight)
  const isWd = dataKey && (dataKey.toLowerCase().startsWith('wd') || dataKey.toLowerCase().includes('_wd'));
  if (isWd) {
    return <WindDirectionArrow cx={cx} cy={cy} value={numericValue} stroke={stroke} size={isActive ? 20 : 16} />;
  }

  // Check if this is a wind speed parameter and direction is also visible
  const wsMatch = dataKey && dataKey.match(/^(.*?)ws(\d+m)Mps$/);
  if (wsMatch) {
    const prefix = wsMatch[1];
    const depth = wsMatch[2];
    const dirKey = `${prefix}wd${depth}Deg`;

    if (props.visibleParams && props.visibleParams[dirKey] && payload && payload[dirKey] !== undefined) {
      const dirValue = typeof payload[dirKey] === 'number' ? payload[dirKey] : parseFloat(payload[dirKey]);
      if (!isNaN(dirValue)) {
        const dirColor = props.allParams?.find(p => p.key === dirKey)?.color || stroke;
        return <WindDirectionArrow cx={cx} cy={cy} value={dirValue} stroke={dirColor} size={isActive ? 20 : 16} />;
      }
    }
  }

  return <circle cx={cx} cy={cy} r={isActive ? 6 : 4} fill={stroke} />;
};

const CollapsibleChartGroup = ({
  title,
  icon,
  parameters,
  data,
  defaultExpanded = true,
  gradientColors,
  topBorderColor,
  chartType,
  minChartWidth,
  paramGroups,
  selectionType = 'checkbox', // 'checkbox' or 'radio'
  trendType,
  selectedStationName
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [chartWidth, setChartWidth] = useState(100); // Percentage width
  const [yZoom, setYZoom] = useState(100); // Percentage vertical zoom (100% = default)

  const chartRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const [downloading, setDownloading] = React.useState(false);
  // Track chart dimensions for dynamic bar sizing
  const [chartDimensions, setChartDimensions] = React.useState({ width: 0, dataLength: 0 });

  // Initial state: if radio, only one should be defaultOn (per group if groups exist)
  const getInitialVisibility = () => {
    const initial = {};
    if (selectionType === 'radio' && paramGroups && paramGroups.length > 0) {
      // Radio per group
      paramGroups.forEach(group => {
        let foundFirst = false;
        group.paramKeys.forEach(key => {
          const param = parameters.find(p => p.key === key);
          if (param?.defaultOn && !foundFirst) {
            initial[key] = true;
            foundFirst = true;
          } else {
            initial[key] = false;
          }
        });
        // Fallback: select first in group if none defaultOn
        if (!foundFirst && group.paramKeys.length > 0) {
          initial[group.paramKeys[0]] = true;
        }
      });
    } else if (selectionType === 'radio') {
      // Radio for entire list
      let foundFirst = false;
      parameters.forEach(param => {
        if (param.defaultOn && !foundFirst) {
          initial[param.key] = true;
          foundFirst = true;
        } else {
          initial[param.key] = false;
        }
      });
      if (!foundFirst && parameters.length > 0) {
        initial[parameters[0].key] = true;
      }
    } else {
      // Checkbox mode
      parameters.forEach(param => {
        initial[param.key] = param.defaultOn || false;
      });
    }
    return initial;
  };

  const [visible, setVisible] = useState(getInitialVisibility());

  const handleToggle = (key) => {
    if (selectionType === 'radio') {
      const newVisible = { ...visible };
      if (paramGroups && paramGroups.length > 0) {
        // Find which group this key belongs to
        const group = paramGroups.find(g => g.paramKeys.includes(key));
        if (group) {
          // Turn off all others in THIS group
          group.paramKeys.forEach(k => {
            newVisible[k] = k === key;
          });
        } else {
          // Fallback for params not in any group
          newVisible[key] = !visible[key];
        }
      } else {
        // Global radio
        parameters.forEach(p => {
          newVisible[p.key] = p.key === key;
        });
      }
      setVisible(newVisible);
    } else {
      setVisible(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  // Helper function to get the correct label for a parameter
  // If paramGroups exist (single station), includes station name
  // Otherwise, uses the original parameter label
  const getParameterLabel = (paramKey) => {
    const param = parameters.find(p => p.key === paramKey);
    if (!param) return paramKey;

    if (paramGroups && paramGroups.length > 0) {
      // Find the group this param belongs to
      const group = paramGroups.find(g => g.paramKeys.includes(paramKey));
      if (group && selectedStationName) {
        // Return label with station name
        return `${param.label} - ${group.label}`;
      }
    }
    // Return original label
    return param.label;
  };

  const cleanTimeString = (timeStr) => {
    if (!timeStr) return null;
    return timeStr.replace(/\[.*?\]/g, "");
  };

  const formatTimeToHours = (timeStr) => {
    if (!timeStr) return '';
    try {
      const cleaned = cleanTimeString(timeStr);
      const date = new Date(cleaned);
      if (isNaN(date.getTime())) return '';

      if (chartType === 'candle' || trendType === 'Weekly') {
        // Return Date for range view or Weekly view
        return cleaned.split('T')[0];
      }

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const processData = () => {
    if (!data || data.length === 0) return [];



    if (trendType === 'Weekly' || (trendType === 'Range' && chartType !== 'candle')) {
      // Aggregate hourly data into daily buckets with OHLC
      const dailyMap = {};

      data.forEach(record => {
        const cleanedTime = cleanTimeString(record.time);
        if (!cleanedTime) return;
        const dateStr = cleanedTime.split('T')[0]; // YYYY-MM-DD

        if (!dailyMap[dateStr]) {
          dailyMap[dateStr] = { records: [] };
        }
        dailyMap[dateStr].records.push(record);
      });

      // Convert map to array and calculate OHLC
      const aggregated = Object.keys(dailyMap).sort().map(date => {
        const dayRecords = dailyMap[date].records;
        const item = { timeLabel: date }; // Use Date as label

        parameters.forEach(p => {
          const values = dayRecords
            .map(r => r[p.key])
            .filter(v => v !== undefined && v !== null && !isNaN(parseFloat(v)))
            .map(v => parseFloat(v));

          if (values.length > 0) {
            const first = values[0];
            const last = values[values.length - 1];
            const max = Math.max(...values);
            const min = Math.min(...values);

            // Store Main Value (Close) for Chart Plotting
            item[p.key] = last;

            // Store OHLC Data for Tooltip
            item[`${p.key}_ohlc`] = {
              open: first,
              high: max,
              low: min,
              close: last
            };
          } else {
            item[p.key] = null;
          }
        });
        return item;
      });

      return aggregated;
    }

    if (chartType === 'candle') {
      // Group by date and aggregate for EACH visible parameter
      const grouped = data.reduce((acc, record) => {
        const cleanedTime = cleanTimeString(record.time);
        const dateStr = cleanedTime ? cleanedTime.split('T')[0] : 'Unknown';
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(record);
        return acc;
      }, {});

      const aggregated = Object.keys(grouped).sort().map(date => {
        const dayRecords = grouped[date];
        const item = { timeLabel: date };

        parameters.forEach(p => {
          const values = dayRecords
            .map(r => r[p.key])
            .filter(v => v !== null && v !== undefined && !isNaN(v));

          if (values.length > 0) {
            const candleData = {
              open: values[0],
              close: values[values.length - 1],
              high: Math.max(...values),
              low: Math.min(...values)
            };
            item[`${p.key}_candle`] = candleData;
            // CRITICAL: Add the numeric close value for Recharts positioning
            item[`${p.key}_candle.close`] = candleData.close;
          }
        });

        return item;
      });

      return aggregated;
    }

    // Default Daily (Hourly) processing
    return data.map(r => {
      const cleaned = { ...r, timeLabel: formatTimeToHours(r.time) };
      parameters.forEach(p => {
        const val = cleaned[p.key];
        if (val !== undefined && val !== null) {
          cleaned[p.key] = typeof val === 'number' ? val : parseFloat(val);
        }
      });
      return cleaned;
    });
  };

  const plotData = processData();

  // Memoize highlights for all visible parameters to avoid redundant calculations
  const allHighlights = React.useMemo(() => {
    const highlights = {};
    parameters.forEach(p => {
      if (visible[p.key]) {
        highlights[p.key] = getHighlights(plotData, p.key);
      }
    });
    return highlights;
  }, [plotData, visible, parameters]);

  // Calculate data ranges for color coding (min/max for each parameter)
  const dataRanges = React.useMemo(() => {
    const ranges = {};
    parameters.forEach(p => {
      const values = plotData
        .filter(d => d[`${p.key}_candle`])
        .flatMap(d => {
          const candle = d[`${p.key}_candle`];
          return [candle.open, candle.close, candle.high, candle.low];
        })
        .filter(v => typeof v === 'number' && !isNaN(v));

      if (values.length > 0) {
        ranges[p.key] = {
          min: Math.min(...values),
          max: Math.max(...values),
          range: Math.max(...values) - Math.min(...values)
        };
      }
    });
    return ranges;
  }, [plotData, parameters]);

  // Helper function to get color based on value intensity - Red/Green only
  const getValueColor = (paramKey, value) => {
    const range = dataRanges[paramKey];
    if (!range || range.range === 0) {
      // Return neutral color if no range
      return '#00cc00'; // Default to green
    }

    // Normalize value to 0-1 scale
    const normalized = (value - range.min) / range.range;

    // Determine if parameter is "inverted" (where low is better)
    const invertedParams = ['rainFallMm', 'ap2mHpa']; // Rain and pressure
    const isInverted = invertedParams.some(p => paramKey.toLowerCase().includes(p.toLowerCase()));

    // Simple Red/Green: normalize to 0.5 threshold
    const intensity = isInverted ? 1 - normalized : normalized;

    // Simple threshold: green if < 0.5, red if >= 0.5
    return intensity < 0.5 ? '#00cc00' : '#ff0000'; // Green for low, Red for high
  };

  const hasVisibleParams = Object.values(visible).some(v => v);

  // Calculate dynamic minTickGap based on zoom level for better date visibility
  const getMinTickGap = () => {
    if (chartWidth <= 70) return 3; // Show fewer dates at low zoom
    if (chartWidth <= 100) return 2; // Show some dates at medium zoom
    if (chartWidth <= 150) return 1; // Show most dates
    return 0; // Show all dates at high zoom
  };

  // Memoize currently visible parameter keys for Y-axis domain calculation
  const visibleKeys = React.useMemo(() => {
    return parameters.filter(p => visible[p.key]).map(p => p.key);
  }, [parameters, visible]);

  // Compute Y axis domain based on visible params and vertical zoom level
  const getYAxisDomain = (keys = visibleKeys) => {
    if (!keys || keys.length === 0) return ['auto', 'auto'];

    let min = Infinity;
    let max = -Infinity;

    keys.forEach(k => {
      const r = dataRanges[k];
      if (r) {
        if (r.min < min) min = r.min;
        if (r.max > max) max = r.max;
      }
    });

    if (!isFinite(min) || !isFinite(max)) return ['auto', 'auto'];

    // Handle flat data
    let range = max - min;
    if (range === 0) {
      const pad = Math.abs(min) * 0.05 || 1;
      return [min - pad, max + pad];
    }

    // yZoom: 100 -> original range, 200 -> zoom in (show half range), 50 -> zoom out (show double range)
    const zoomFactor = yZoom / 100;
    const mid = (min + max) / 2;
    const half = (range / 2) / zoomFactor;
    const pad = range * 0.05; // small extra padding

    return [mid - half - pad, mid + half + pad];
  };

  // Generate Y-axis ticks with more points for better accuracy
  const getYAxisTicks = (keys = visibleKeys) => {
    const domain = getYAxisDomain(keys);
    if (domain[0] === 'auto' || domain[1] === 'auto') return undefined;
    const [min, max] = domain;
    const range = max - min;
    if (range === 0) return undefined;

    // Generate more ticks based on zoom level: more zoom = more detail
    const baseTickCount = 12; // Increased from 9
    const zoomMultiplier = Math.max(1, yZoom / 100);
    const tickCount = Math.floor(baseTickCount * zoomMultiplier);

    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      const value = min + (range / tickCount) * i;
      ticks.push(value);
    }
    return ticks;
  };

  // Calculate dynamic bar size based on chart width and data length
  const getBarSize = () => {
    const baseSize = 35;
    const { width, dataLength } = chartDimensions;

    if (!width || !dataLength) return baseSize;

    // Calculate available width per data point
    const widthPerPoint = (width * (chartWidth / 100)) / dataLength;

    // Dynamic sizing: more zoom = wider bars, but cap at reasonable limits
    const dynamicSize = Math.max(8, Math.min(80, widthPerPoint * 0.6));

    return dynamicSize;
  };

  const CustomTooltip = ({ active, payload, label, coordinate, viewBox }) => {
    if (active && payload && payload.length) {
      const timeValue = label || (payload[0]?.payload?.timeLabel) || 'N/A';

      // 1. Smart Positioning Logic
      const chartWidth = viewBox?.width || 0;
      const chartHeight = viewBox?.height || 0;
      const x = coordinate?.x || 0;
      const y = coordinate?.y || 0;

      const isRightHalf = x > chartWidth / 2;
      const isBottomHalf = y > chartHeight / 2;

      const xOffset = 20;
      const yOffset = 20;

      // Fixed Styling relative to the data point
      const tooltipStyle = {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[4],
        padding: '8px',
        maxWidth: '500px',
        maxHeight: '300px',
        overflowY: 'auto',
        width: 'max-content',
        zIndex: 9999,
        pointerEvents: 'none',
        // transform: `translate(${translateX}, ${translateY})`,
        transition: 'transform 0.05s ease-out',
      };

      // 2. Data Preparation for Table
      // Check if we have any OHLC data to decide table columns
      const hasOHLC = payload.some(entry =>
        chartType === 'candle' ||
        entry.payload[`${entry.dataKey}_ohlc`]
      );

      return (
        <Box sx={tooltipStyle}>
          {/* Header */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, borderBottom: `1px solid ${theme.palette.divider}`, pb: 0.5 }}>
            {chartType === 'candle' ? 'Date' : 'Time'}: {timeValue}
          </Typography>

          {/* Table Content */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>
                <th style={{ padding: '4px', whiteSpace: 'nowrap' }}>Parameter</th>
                {hasOHLC ? (
                  <>
                    <th style={{ padding: '4px', textAlign: 'right' }}>Open</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>High</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>Low</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>Close</th>
                  </>
                ) : (
                  <th style={{ padding: '4px', textAlign: 'right' }}>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {payload.map((entry, idx) => {
                // Extract Data
                let paramName = entry.name;
                let o, h, l, c, val;
                let color = entry.color;
                // eslint-disable-next-line no-unused-vars
                let isWd = false;

                // Handle Candle Data
                if (chartType === 'candle') {
                  const candleKey = entry.dataKey.replace('.close', '');
                  const candle = entry.payload[candleKey];
                  if (!candle) return null;
                  const param = parameters.find(p => p.key === candleKey.replace('_candle', ''));
                  if (!param) return null;

                  paramName = param.label;
                  color = getValueColor(param.key, candle.close);
                  o = candle.open;
                  h = candle.high;
                  l = candle.low;
                  c = candle.close;
                }
                // Handle OHLC Data (Weekly/Range Line Charts)
                else if (entry.payload[`${entry.dataKey}_ohlc`]) {
                  const ohlc = entry.payload[`${entry.dataKey}_ohlc`];
                  o = ohlc.open;
                  h = ohlc.high;
                  l = ohlc.low;
                  c = ohlc.close;
                }
                // Handle Standard Value
                else {
                  val = entry.value;
                }
                // Detect only windspeed at  for all depth
                const isWindSpeed = entry.dataKey && /ws\d+mMps$/i.test(entry.dataKey);
                // DAILY notation
                const windIndicator =
                  isWindSpeed && !isNaN(parseFloat(val))
                    ? getWindInfo(parseFloat(val)).indicator
                    : null;

                return (
                  <React.Fragment key={`row-${idx}`}>
                    <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <td style={{ padding: '4px', color: color, fontWeight: 600 }}>
                        {paramName}
                      </td>

                      {hasOHLC ? (
                        <>
                          <td style={{ padding: '4px', textAlign: 'right' }}>
                            {typeof o === 'number' ? o.toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'right', color: '#d32f2f' }}>
                            {typeof h === 'number' ? h.toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'right', color: '#1976d2' }}>
                            {typeof l === 'number' ? l.toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700 }}>
                            {typeof c === 'number' ? c.toFixed(2) : '-'}
                          </td>
                        </>
                      ) : (
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700 }}>
                          {typeof val === 'number' ? val.toFixed(2) : val}
                        </td>
                      )}
                    </tr>

                    {/* DAILY WIND NOTATION */}
                    {!hasOHLC && windIndicator && (
                      <tr>
                        <td style={{ padding: '4px', fontStyle: 'italic', color: theme.palette.text.secondary }}>
                          Notation
                        </td>
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>
                          {windIndicator}
                        </td>
                      </tr>
                    )}

                    {/* WEEKLY WIND NOTATION */}
                    {hasOHLC && isWindSpeed && (
                      <tr>
                        <td style={{ padding: '4px', fontStyle: 'italic', color: theme.palette.text.secondary }}>
                          Notation
                        </td>

                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600 }}>
                          {!isNaN(parseFloat(o)) ? getWindInfo(parseFloat(o)).indicator : "--"}
                        </td>

                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600 }}>
                          {!isNaN(parseFloat(h)) ? getWindInfo(parseFloat(h)).indicator : "--"}
                        </td>

                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600 }}>
                          {!isNaN(parseFloat(l)) ? getWindInfo(parseFloat(l)).indicator : "--"}
                        </td>

                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600 }}>
                          {!isNaN(parseFloat(c)) ? getWindInfo(parseFloat(c)).indicator : "--"}
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      );
    }
    return null;
  };

  // Optimize: Calculate domain once per render
  const axisDomain = getYAxisDomain();

  return (
    <Paper
      elevation={2}
      sx={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderTop: topBorderColor ? `4px solid ${topBorderColor}` : 'none',
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: theme.shadows[4] }
      }}
    >
      {/* Calculate Domain Once per Render for Performance */}
      {(() => {
        // This is a bit hacky place, let's put it properly inside the function body
        return null;
      })() || null}

      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: expanded ? theme.spacing(1.5) : 0,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <span style={{ fontSize: '1.5rem' }}>{icon}</span>
          <Box
            component="h3"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
              margin: 0,
              background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {title}
          </Box>
        </Box>
        <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout={300}>
        <Box>
          {/* Group Toggles (New Layout) */}
          {paramGroups && paramGroups.length > 0 ? (
            <Box sx={{ marginBottom: theme.spacing(2) }}>
              {paramGroups.map(group => {
                const allChecked = group.paramKeys.every(key => visible[key]);
                const someChecked = group.paramKeys.some(key => visible[key]);
                const indeterminate = someChecked && !allChecked;

                // Get visual style from the first parameter of this group
                const firstParamKey = group.paramKeys[0];
                const firstParam = parameters.find(p => p.key === firstParamKey);
                const dashArray = firstParam?.strokeDasharray || "";

                return (
                  <Box
                    key={group.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 1,
                      gap: 2,
                      flexWrap: 'nowrap'
                    }}
                  >
                    {/* 1. Checkbox / Selection Label */}
                    {selectionType === 'checkbox' ? (
                      <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          const newVisible = { ...visible };
                          group.paramKeys.forEach(key => {
                            newVisible[key] = newValue;
                          });
                          setVisible(newVisible);
                        }}
                        size="medium"
                        sx={{ padding: 0 }}
                      />
                    ) : (
                      <Box sx={{ width: 42 }} />
                    )}

                    {/* 2. Station Name */}
                    <Box sx={{ minWidth: 120, fontWeight: 700, fontSize: '0.9rem' }}>
                      {group.label}
                    </Box>

                    {/* 3. Legend Line */}
                    <Box sx={{ width: 40, display: 'flex', alignItems: 'center' }}>
                      <svg width="40" height="4">
                        <line x1="0" y1="2" x2="40" y2="2" stroke="black" strokeWidth="3" strokeDasharray={dashArray} />
                      </svg>
                    </Box>

                    {/* 4. Parameter List */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 0, columnGap: 1.5, alignItems: 'center' }}>
                      {group.paramKeys.map(key => {
                        const param = parameters.find(p => p.key === key);
                        const isVisible = visible[key];
                        if (!param) return null;

                        return (
                          <FormControlLabel
                            key={key}
                            control={
                              selectionType === 'radio' ? (
                                <Radio
                                  checked={isVisible}
                                  onChange={() => handleToggle(key)}
                                  size="small"
                                  sx={{
                                    padding: '2px',
                                    color: param.color,
                                    '&.Mui-checked': { color: param.color }
                                  }}
                                />
                              ) : (
                                <Checkbox
                                  checked={isVisible}
                                  onChange={() => handleToggle(key)}
                                  size="small"
                                  sx={{
                                    padding: '2px',
                                    color: param.color,
                                    '&.Mui-checked': { color: param.color }
                                  }}
                                />
                              )
                            }
                            label={
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: isVisible ? 'text.primary' : 'text.disabled',
                                  marginLeft: '2px'
                                }}
                              >
                                {param.shortLabel || param.label}
                              </Box>
                            }
                            sx={{ margin: 0, paddingRight: 1 }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {/* Render legacy parameter list only if NO groups are present */}
          {(!paramGroups || paramGroups.length === 0) && (
            <Box sx={{ marginBottom: theme.spacing(1.5) }}>
              {selectionType === 'checkbox' && (
                <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                  <Box
                    component="span"
                    onClick={() => {
                      const newVisible = {};
                      parameters.forEach(p => newVisible[p.key] = true);
                      setVisible(newVisible);
                    }}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: gradientColors[0],
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      '&:hover': { textDecoration: 'underline', opacity: 0.8 }
                    }}
                  >
                    Select All
                  </Box>
                  <Box component="span" sx={{ fontSize: '0.8rem', color: theme.palette.divider }}>|</Box>
                  <Box
                    component="span"
                    onClick={() => {
                      const newVisible = {};
                      parameters.forEach(p => newVisible[p.key] = false);
                      setVisible(newVisible);
                    }}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: gradientColors[0],
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      '&:hover': { textDecoration: 'underline', opacity: 0.8 }
                    }}
                  >
                    Deselect All
                  </Box>
                </Box>
              )}

              {selectionType === 'checkbox' ? (
                <FormGroup row sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                  {parameters.map(param => (
                    <FormControlLabel
                      key={param.key}
                      control={
                        <Checkbox
                          checked={visible[param.key]}
                          onChange={() => handleToggle(param.key)}
                          sx={{
                            color: param.color,
                            '&.Mui-checked': { color: param.color },
                            padding: '4px'
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Box component="span" sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                          {param.label}
                        </Box>
                      }
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </FormGroup>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {parameters.map(param => (
                    <FormControlLabel
                      key={param.key}
                      control={
                        <Radio
                          checked={visible[param.key]}
                          onChange={() => handleToggle(param.key)}
                          sx={{
                            color: param.color,
                            '&.Mui-checked': { color: param.color },
                            padding: '4px'
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Box
                          component="span"
                          sx={{
                            fontSize: '0.75rem',
                            color: visible[param.key] ? 'text.primary' : 'text.secondary',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {param.label}
                        </Box>
                      }
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {hasVisibleParams && plotData.length > 0 ? (
            <Box>
              {/* Zoom Controls */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 600 }}>X-Axis Zoom:</Box>
                <button
                  onClick={() => setChartWidth(Math.max(50, chartWidth - 10))}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  − Zoom Out
                </button>
                <Box sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, minWidth: '50px', textAlign: 'center' }}>
                  {chartWidth}%
                </Box>
                <button
                  onClick={() => setChartWidth(Math.min(300, chartWidth + 10))}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  + Zoom In
                </button>
                <button
                  onClick={() => setChartWidth(100)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: theme.palette.secondary.main,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Reset
                </button>

                <Box sx={{ width: 12 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Y-Axis Zoom:</Box>
                  <button
                    onClick={() => setYZoom(Math.max(50, yZoom - 10))}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: theme.palette.primary.dark,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    −
                  </button>
                  <Box sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, minWidth: '50px', textAlign: 'center' }}>
                    {yZoom}%
                  </Box>
                  <button
                    onClick={() => setYZoom(Math.min(300, yZoom + 10))}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: theme.palette.primary.dark,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    +
                  </button>
                  <button
                    onClick={() => setYZoom(100)}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: theme.palette.secondary.dark,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    Reset
                  </button>
                </Box>
              </Box>

              {/* Chart Container with Scroll */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  paddingBottom: '10px',
                  display: 'block',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px'
                }}
                ref={scrollContainerRef}
              ><button
                disabled={downloading}
                onClick={async () => {
                  setDownloading(true);
                  await DownloadGraph(chartRef, title);
                  setDownloading(false);
                }}
                style={{
                  padding: "6px 12px",
                  backgroundColor: downloading ? "#999" : "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600
                }}
              >
                  {downloading ? "Downloading..." : "Download Graph"}
                </button>
                <Box
                  ref={chartRef}
                  sx={{
                    height: 600,
                    minWidth: `${chartWidth}%`,
                    width: `${chartWidth}%`,
                    position: 'relative'
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    minWidth={100}
                    debounce={50}
                    onResize={(width) => {
                      setChartDimensions({ width, dataLength: plotData.length });
                    }}
                  >
                    {chartType === 'candle' ? (
                      <ComposedChart
                        data={plotData}
                        margin={{ top: 25, right: 30, left: 60, bottom: 70 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                          dataKey="timeLabel"
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 9, angle: -45, textAnchor: 'end', height: 70 }}
                          minTickGap={getMinTickGap()}
                          label={{ value: 'Date', position: 'bottom', offset: 5, style: { fill: theme.palette.text.secondary, fontSize: '12px' } }}
                          interval={trendType === 'Weekly' ? 0 : 'preserveStartEnd'}
                          tickFormatter={(val) => {
                            if ((trendType === 'Weekly' || trendType === 'Range') && val) {
                              try { return format(new Date(val), 'dd MMM'); } catch (e) { return val; }
                            }
                            return val;
                          }}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                          domain={getYAxisDomain()}
                          ticks={getYAxisTicks()}
                          tickFormatter={(value) => {
                            const range = Math.abs(value);
                            if (range >= 100) return value.toFixed(0);
                            if (range >= 10) return value.toFixed(1);
                            return value.toFixed(2);
                          }}
                          padding={{ top: 30, bottom: 10 }}
                          label={{ value: 'Values', angle: -90, position: 'insideLeft', style: { fill: theme.palette.text.secondary } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '0.7rem', paddingTop: '10px' }} iconSize={8} />

                        {parameters.map(param => {
                          const isWd = param.key.toLowerCase().startsWith('wd') || param.key.toLowerCase().includes('_wd');
                          if (!visible[param.key] || isWd) return null;

                          const candleDataKey = `${param.key}_candle`;
                          const paramLabel = getParameterLabel(param.key);

                          return (
                            <Bar
                              key={param.key}
                              dataKey={`${param.key}_candle.close`}
                              name={paramLabel}
                              fill="transparent"
                              isAnimationActive={false} // Disable animation to prevent layout jumping
                              shape={(props) => {
                                const { x, y, width, payload } = props;

                                if (!payload || !payload[candleDataKey]) {
                                  return null;
                                }

                                const candle = payload[candleDataKey];
                                const { open, close, high, low } = candle;

                                if ([open, close, high, low].some(v => typeof v !== 'number' || isNaN(v))) {
                                  return null;
                                }

                                // Use pre-calculated domain
                                const domain = axisDomain;
                                if (!domain || domain[0] === 'auto' || domain[1] === 'auto') {
                                  return null;
                                }

                                const [minY, maxY] = domain;
                                const range = maxY - minY;
                                if (range === 0) return null;

                                // Hybrid Anchoring: use Recharts-provided `y` (close pixel) as anchor.
                                // This keeps values synchronized with the Y axis regardless of container height.
                                const yClose = y;

                                // Calculate scale factor using actual plot height
                                // ComposedChart margins: top 25 + bottom 70 = 95 pixels total
                                // Chart container height is 600px, so plot area = 600 - 95 = 505px
                                const CHART_HEIGHT = 600;
                                const CHART_TOP_MARGIN = 25;
                                const CHART_BOTTOM_MARGIN = 70;
                                const plotHeight = CHART_HEIGHT - CHART_TOP_MARGIN - CHART_BOTTOM_MARGIN; // 505 pixels
                                const pixelsPerUnit = plotHeight / range;

                                // Derive other points relative to Close value
                                // Note: SVG Y increases downward. Higher data value -> smaller pixel Y.
                                const yOpen = yClose - (open - close) * pixelsPerUnit;
                                const yHigh = yClose - (high - close) * pixelsPerUnit;
                                const yLow = yClose - (low - close) * pixelsPerUnit;

                                const bodyTop = Math.min(yOpen, yClose);
                                const bodyHeight = Math.abs(yOpen - yClose);
                                const capWidth = width * 0.6;

                                // Determine candle color based on bullish/bearish rules
                                // Green (bullish) when Close > Open
                                // Red (bearish) when Close < Open
                                const isRising = close > open;
                                const candleColor = isRising ? '#00cc00' : '#ff0000'; // Green for bullish, Red for bearish

                                return (
                                  <g>
                                    {/* Wick */}
                                    <line
                                      x1={x + width / 2}
                                      y1={yHigh}
                                      x2={x + width / 2}
                                      y2={yLow}
                                      stroke={candleColor}
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                    />
                                    {/* Top Cap */}
                                    <line
                                      x1={x + width / 2 - capWidth / 2}
                                      y1={yHigh}
                                      x2={x + width / 2 + capWidth / 2}
                                      y2={yHigh}
                                      stroke={candleColor}
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                    />
                                    {/* Bottom Cap */}
                                    <line
                                      x1={x + width / 2 - capWidth / 2}
                                      y1={yLow}
                                      x2={x + width / 2 + capWidth / 2}
                                      y2={yLow}
                                      stroke={candleColor}
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                    />
                                    {/* Body */}
                                    <rect
                                      x={x}
                                      y={bodyTop}
                                      width={width}
                                      height={Math.max(2, bodyHeight)}
                                      fill={candleColor}
                                      stroke={candleColor}
                                      strokeWidth="2"
                                    />
                                  </g>
                                );
                              }}
                              barSize={getBarSize()}
                            />
                          );
                        })}

                        {/* Wind Direction Arrows for Candle Charts */}
                        {parameters.map(param => {
                          const isWd = param.key.toLowerCase().startsWith('wd') || param.key.toLowerCase().includes('_wd');
                          if (!visible[param.key] || !isWd) return null;
                          const paramLabel = getParameterLabel(param.key);

                          return (
                            <Bar
                              key={`${param.key}-arrow`}
                              dataKey={param.key}
                              name={paramLabel}
                              yAxisId="main"
                              shape={(props) => {
                                const { x, y, width, payload } = props;
                                const direction = payload[param.key];
                                if (direction === null || direction === undefined) return null;

                                const angle = parseFloat(direction) || 0;
                                const cx = x + width / 2;
                                const cy = y;
                                const arrowSize = Math.min(width, 30);
                                const color = param.color;

                                return (
                                  <g transform={`translate(${cx}, ${cy})`}>
                                    {/* Arrow pointing in direction */}
                                    <g transform={`rotate(${angle})`}>
                                      {/* Arrow head triangle */}
                                      <polygon
                                        points={`0,-${arrowSize / 2} ${arrowSize / 3},${arrowSize / 4} -${arrowSize / 3},${arrowSize / 4}`}
                                        fill={color}
                                        stroke={color}
                                        strokeWidth="1"
                                      />
                                      {/* Arrow shaft */}
                                      <line
                                        x1="0"
                                        y1={-arrowSize / 2}
                                        x2="0"
                                        y2={arrowSize / 4}
                                        stroke={color}
                                        strokeWidth="2"
                                      />
                                      {/* Center circle */}
                                      <circle cx="0" cy="0" r="3" fill={color} />
                                    </g>
                                    {/* Direction text */}
                                    <text
                                      x="0"
                                      y={arrowSize / 2 + 15}
                                      textAnchor="middle"
                                      fill={color}
                                      fontSize="10"
                                      fontWeight="bold"
                                    >
                                      {Math.round(angle)}°
                                    </text>
                                  </g>
                                );
                              }}
                              isAnimationActive={false}
                            />
                          );
                        })}
                      </ComposedChart>
                    ) : chartType === 'bar' ? (
                      <BarChart data={plotData} margin={{ top: 25, right: 30, left: 60, bottom: 70 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                          dataKey="timeLabel"
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 9, angle: -45, textAnchor: 'end', height: 70 }}
                          minTickGap={getMinTickGap()}
                          label={{ value: 'Date', position: 'bottom', offset: 5, style: { fill: theme.palette.text.secondary, fontSize: '12px' } }}
                          interval={trendType === 'Weekly' ? 0 : 'preserveStartEnd'}
                          tickFormatter={(val) => {
                            if ((trendType === 'Weekly' || trendType === 'Range') && val) {
                              try { return format(new Date(val), 'dd MMM'); } catch (e) { return val; }
                            }
                            return val;
                          }}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                          domain={getYAxisDomain()}
                          ticks={getYAxisTicks()}
                          tickFormatter={(value) => value.toFixed(3)}
                          padding={{ top: 30, bottom: 10 }}
                          label={{ value: 'Values', angle: -90, position: 'insideLeft', style: { fill: theme.palette.text.secondary } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '0.7rem', paddingTop: '10px' }} iconSize={8} />

                        {parameters.map(param => {
                          const isWd = param.key.toLowerCase().startsWith('wd') || param.key.toLowerCase().includes('_wd');
                          return visible[param.key] && !isWd && (
                            <Bar key={param.key} dataKey={param.key} fill={param.color} name={param.label} isAnimationActive={false}>
                              {plotData.map((entry, index) => {
                                const highlights = getHighlights(plotData, param.key);
                                let color = param.color;
                                if (index === highlights.max) color = "#d32f2f";
                                else if (index === highlights.min) color = "#1976d2";
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                              <LabelList
                                dataKey={param.key}
                                position="top"
                                content={(props) => {
                                  const { x, y, width, index } = props;
                                  const highlights = getHighlights(plotData, param.key);
                                  let label = "";
                                  let color = param.color;

                                  if (index === highlights.max) {
                                    label = "MAX";
                                    color = "#d32f2f";
                                  } else if (index === highlights.min) {
                                    label = "MIN";
                                    color = "#1976d2";
                                  } else if (index === highlights.start) {
                                    label = "START";
                                    color = "#455a64";
                                  } else if (index === highlights.end) {
                                    label = "END";
                                    color = "#455a64";
                                  }

                                  if (!label) return null;

                                  return (
                                    <text
                                      x={x + width / 2}
                                      y={y - 8}
                                      fill={color}
                                      textAnchor="middle"
                                      style={{ fontSize: '9px', fontWeight: 'bold' }}
                                    >
                                      {label}
                                    </text>
                                  );
                                }}
                              />
                              <LabelList
                                dataKey={param.key}
                                position="top"
                                content={(props) => {
                                  const { x, y, width, value } = props;

                                  if (!param.key.toLowerCase().includes("ws")) return null;

                                  const indicator = getWindInfo(value).indicator
                                  if (!indicator) return null;

                                  return (
                                    <text
                                      x={x + width / 2}
                                      y={y - 20}
                                      textAnchor="middle"
                                      fill="#000"
                                      style={{ fontSize: "9px", fontWeight: "bold" }}
                                    >
                                      {indicator}
                                    </text>
                                  );
                                }}
                              />
                            </Bar>
                          );
                        })}
                      </BarChart>
                    ) : (
                      <LineChart data={plotData} margin={{ top: 40, right: 30, left: 60, bottom: 70 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                          dataKey="timeLabel"
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 9, angle: -45, textAnchor: 'end', height: 70 }}
                          minTickGap={getMinTickGap()}
                          label={{ value: 'Date', position: 'bottom', offset: 5, style: { fill: theme.palette.text.secondary, fontSize: '12px' } }}
                          interval={trendType === 'Weekly' ? 0 : 'preserveStartEnd'}
                          tickFormatter={(val) => {
                            if ((trendType === 'Weekly' || trendType === 'Range') && val) {
                              try { return format(new Date(val), 'dd MMM'); } catch (e) { return val; }
                            }
                            return val;
                          }}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                          domain={getYAxisDomain()}
                          ticks={getYAxisTicks()}
                          tickFormatter={(value) => value.toFixed(3)}
                          padding={{ top: 30, bottom: 10 }}
                          label={{ value: 'Values', angle: -90, position: 'insideLeft', style: { fill: theme.palette.text.secondary } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '0.7rem', paddingTop: '10px' }} iconSize={8} />

                        {parameters.map(param => {
                          const isWd = param.key.toLowerCase().startsWith('wd') || param.key.toLowerCase().includes('_wd');
                          return visible[param.key] && !isWd && (
                            <Line
                              key={param.key}
                              type="monotone"
                              dataKey={param.key}
                              stroke={param.color}
                              strokeWidth={param.strokeWidth || 2}
                              strokeDasharray={param.strokeDasharray}
                              dot={
                                <CustomizedDot
                                  seriesData={plotData}
                                  totalPoints={plotData.length}
                                  highlights={allHighlights[param.key]}
                                  visibleParams={visible}
                                  allParams={parameters}
                                />
                              }
                              activeDot={(props) => (
                                <CustomizedDot
                                  {...props}
                                  seriesData={plotData}
                                  totalPoints={plotData.length}
                                  highlights={allHighlights[param.key]}
                                  isActive={true}
                                  visibleParams={visible}
                                  allParams={parameters}
                                />
                              )}
                              name={param.label}
                              isAnimationActive={false}
                            >

                              {/* 🆕 WIND SPEED INDICATOR */}
                              <LabelList
                                dataKey={param.key}
                                position="top"
                                content={(props) => {
                                  const { x, y, value } = props;

                                  // apply only to wind speed parameters
                                  if (!param.key.toLowerCase().includes("ws")) return null;

                                  const indicator = getWindInfo(value).indicator
                                  if (!indicator) return null;

                                  return (
                                    <text
                                      x={x}
                                      y={y - 15}
                                      textAnchor="middle"
                                      fill="#000"
                                      style={{ fontSize: "9px", fontWeight: "bold" }}
                                    >
                                      {indicator}
                                    </text>
                                  );
                                }}
                              />

                            </Line>
                          );
                        })}
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                padding: theme.spacing(4),
                color: theme.palette.text.secondary,
                fontSize: '0.9rem',
                backgroundColor: theme.palette.background.default,
              }}
            >
              {hasVisibleParams ? 'No data available' : 'Select parameters to view chart'}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleChartGroup;